import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { queryKey, type CommandInput } from "../../../api/endpoints";
import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { SmeCardView } from "../../../api/types";
import { lineWaitsAlone } from "../shared/waits";

/** What a link or the stepper puts in the address state when the SME opens a card by their own action. */
export interface ShownState {
  shown?: boolean;
}

export function cardQueryKey(elementId: string): QueryKey {
  return queryKey("sme_card", { element_id: elementId }) as unknown as QueryKey;
}

type Display = (input: CommandInput<"display_card">) => Promise<{ data: SmeCardView }>;

/** Record the card as displayed, then the lines that wait on their own. The first failure is thrown; a line that fails is left. */
async function recordDisplay(display: Display, queryClient: QueryClient, card: SmeCardView): Promise<void> {
  const element = card.element;
  const first = await display({ element_id: element.id, version: element.version });
  queryClient.setQueryData(cardQueryKey(element.id), first.data);
  for (const line of [...card.library_lines, ...card.lines].filter((candidate) => lineWaitsAlone(candidate, element))) {
    try {
      const reply = await display({ element_id: element.id, line_id: line.id, version: line.version });
      queryClient.setQueryData(cardQueryKey(element.id), reply.data);
    } catch {
      // A line that cannot be recorded now is left; its own answer is refused with the reason if it is needed.
    }
  }
}

/**
 * Records that the SME has looked at the card (`display_card`), which is what lets Validate open
 * (DEC-1016-97, point 9). A card the SME opened by their own action (a click in the inbox or the stepper) is
 * recorded as soon as it is on the screen. A card reached any other way (a reload, a pasted address, a pointer
 * from the agent) waits for the SME to press "Show the card". A card that changed after it was shown goes back to
 * waiting for that press. Lines that wait on their own are recorded with it.
 */
export function useCardDisplay(card: SmeCardView | undefined) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { mutateAsync, isPending } = useApiCommand("display_card");
  const [error, setError] = useState<string | null>(null);
  const handled = useRef<string | null>(null);
  const byHand = (location.state as ShownState | null)?.shown === true;

  const show = useCallback((): Promise<void> => {
    if (!card) return Promise.resolve();
    return recordDisplay(mutateAsync, queryClient, card).then(
      () => setError(null),
      (failure: unknown) => setError(refusalText(failure)),
    );
  }, [card, mutateAsync, queryClient]);

  useEffect(() => {
    if (!card || !byHand) return;
    const key = `${location.key}:${card.element.id}`;
    if (handled.current === key) return;
    handled.current = key;
    // The address state is used once: a reload does not count as the SME's own action.
    void navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    if (!card.displayed) void show();
  }, [card, byHand, location.key, location.pathname, location.search, navigate, show]);

  return { show, busy: isPending, error };
}
